/**
 * Plain async orchestration for "Generate with Astra": stream the agent's
 * answer to completion, then parse it into the review flow's
 * {@link GeneratedQuestionsResult}.
 *
 * Lives in `logic/` (not `hooks/`) so the `await` orchestration stays out of
 * the hooks layer — the same split the publish flow uses
 * (`publishReviewQuestion`). The hook ({@link useGenerateQuestions}) wraps this
 * in a mutation for in-flight/error state; this function is React-free and
 * directly unit-testable.
 */
import { collectAgentStream, type AgentToolFrame } from 'src/data/api/streamAgent';
import {
  parseGeneratedQuestions,
  type GeneratedQuestionsResult,
} from './parseGeneratedQuestions';

/** The AuditWhiz agent id routed by the Starlight proxy (`/agent/auditwhiz/stream`). */
export const AUDITWHIZ_AGENT_ID = 'auditwhiz';

/** The only `task` the agent accepts today. Sent in `agentInputs`. */
const GENERATE_TASK = 'generate';

/**
 * Idle window for a generation turn. A successful generation runs 60–90s with
 * long gaps between the tool phase and the first answer token, so the transport
 * default (60s) is too tight. This raises it while staying well under the
 * Starlight ALB's 240s cap, so the client still gives up with a clear error
 * before the infra silently drops the socket.
 */
export const GENERATE_IDLE_TIMEOUT_MS = 180_000;

export interface GenerateReviewQuestionsInput {
  /** Free-text prompt/context (1–50000 chars; required even with a SOP number). */
  readonly prompt: string;
  /** Active business unit (scopes SOP search). */
  readonly businessUnitId: string;
  /** Optional SOP number — omit to let the agent choose one from the prompt. */
  readonly sopNumber?: string | undefined;
  /** Stable per-turn session id (`^[A-Za-z0-9-_]+$`, 8–200 chars). */
  readonly sessionId: string;
  /** Optional progress callback — forwarded the agent's `tool` frames for a
   *  friendly step indicator while the (60–90s) generation runs. */
  readonly onProgress?: (frame: AgentToolFrame) => void;
  /** Optional caller signal to abort an in-flight generation (e.g. the user
   *  re-generates or leaves the screen). Composed with the transport's idle
   *  timeout. */
  readonly signal?: AbortSignal;
}

/**
 * Generates review questions end to end: opens the agent stream, accumulates
 * the answer, and parses it. Resolves with the mapped
 * {@link GeneratedQuestionsResult}; rejects (propagating the transport/parse
 * error) so the caller keeps the user on the prompt screen and surfaces the
 * message.
 */
export async function generateReviewQuestions(
  input: GenerateReviewQuestionsInput,
): Promise<GeneratedQuestionsResult> {
  const { prompt, businessUnitId, sopNumber, sessionId, onProgress, signal } = input;

  const answerText = await collectAgentStream({
    agentId: AUDITWHIZ_AGENT_ID,
    prompt,
    businessUnitId,
    sessionId,
    agentInputs: {
      task: GENERATE_TASK,
      ...(sopNumber ? { sop_number: sopNumber } : {}),
    },
    idleTimeoutMs: GENERATE_IDLE_TIMEOUT_MS,
    ...(onProgress ? { onProgress } : {}),
    ...(signal ? { signal } : {}),
  });

  return parseGeneratedQuestions(answerText, { businessUnit: businessUnitId });
}
